import { FillLayer, CircleLayer, SymbolLayer } from "react-map-gl";
import { Theme } from "@mui/material/styles";

type MapLayers = {
  base: FillLayer;
  hover: FillLayer;
  select: FillLayer;
}

export const getAreaLayers = (theme:Theme): MapLayers => {
  return {
    base: {
      id: "area-base",
      type: "fill",
      "source-layer": "postal_area_test",
      maxzoom: 7,
      minzoom: 4,
      paint: {
        "fill-outline-color": "rgba(33,43,58,0.4)",
        "fill-color": [
          "case",
          ["==", ["feature-state", "score"], null],
          "#ECF3F4",
          [
            "interpolate",
            ["linear"],
            ["feature-state", "score"],
            0,
            theme.palette.gradient[0],
            0.1,
            theme.palette.gradient[1],
            0.2,
            theme.palette.gradient[2],
            0.3,
            theme.palette.gradient[3],
            0.4,
            theme.palette.gradient[4],
          ],
        ],
        "fill-opacity": [
          "case",
          ["==", ["feature-state", "score"], null],
          0,
          ["case", ["<", ["feature-state", "score"], 0], 0, 1],
        ],
      },
    },
    hover: {
      id: "area-hover",
      type: "fill",
      "source-layer": "postal_area_test",
      maxzoom: 7,
      minzoom: 4,
      paint: {
        // "fill-outline-color": "#FFFFFF",
        "fill-color": "#FFFFFF",
        "fill-opacity": 0.3,
      },
    },
    select: {
      id: "area-select",
      type: "fill",
      "source-layer": "postal_area_test",
      maxzoom: 7,
      minzoom: 4,
      paint: {
        // "fill-outline-color": "#F5B49F",
        "fill-color": "#F5B49F",
        "fill-opacity": 1.0,
      },
    }
  }
}

export const getDistrictLayers = (theme:Theme): MapLayers => {
  return {
    base: {
      id: "district-base",
      type: "fill",
      "source-layer": "postal_district_test",
      maxzoom: 9,
      minzoom: 7,
      paint: {
        "fill-outline-color": "rgba(33,43,58,0.4)",
        "fill-color": [
          "interpolate",
          ["linear"],
          ["feature-state", "score"],
          // 0, '#212b3a',
          0,
          theme.palette.gradient[0],
          0.1,
          theme.palette.gradient[1],
          0.2,
          theme.palette.gradient[2],
          0.4,
          theme.palette.gradient[3],
          0.6,
          theme.palette.gradient[4],
        ],
        "fill-opacity": [
          "case",
          ["==", ["feature-state", "score"], null],
          0,
          ["case", ["<", ["feature-state", "score"], 0], 0, 1],
        ],
      },
    },
    hover: {
      id: "district-hover",
      type: "fill",
      "source-layer": "postal_district_test",
      maxzoom: 9,
      minzoom: 7,
      paint: {
        // "fill-outline-color": "#FFFFFF",
        "fill-color": "#FFFFFF",
        "fill-opacity": 0.3,
      },
    },
    select: {
      id: "district-select",
      type: "fill",
      "source-layer": "postal_district_test",
      maxzoom: 9,
      minzoom: 7,
      paint: {
        // "fill-outline-color": "#F5B49F",
        "fill-color": "#F5B49F",
        "fill-opacity": 1.0,
      },
    }
  }
}

export const getSectorLayers = (theme:Theme): MapLayers => {
  return {
    base: {
      id: "sector-base",
      type: "fill",
      "source-layer": "postal_sector_test",
      minzoom: 9,
      paint: {
        "fill-outline-color": "rgba(33,43,58,0.4)",
        "fill-color": [
          "interpolate",
          ["linear"],
          ["feature-state", "score"],
          0,
          theme.palette.gradient[0],
          0.2,
          theme.palette.gradient[1],
          0.4,
          theme.palette.gradient[2],
          0.6,
          theme.palette.gradient[3],
          0.8,
          theme.palette.gradient[4],
        ],
        "fill-opacity": [
          "case",
          ["==", ["feature-state", "score"], null],
          0,
          ["case", ["<", ["feature-state", "score"], 0], 0, 1],
        ],
      },
    },
    hover: {
      id: "sector-hover",
      type: "fill",
      "source-layer": "postal_sector_test",
      minzoom: 9,
      paint: {
        // "fill-outline-color": "#FFFFFF",
        "fill-color": "#FFFFFF",
        "fill-opacity": 0.3,
      },
    },
    select: {
      id: "sector-select",
      type: "fill",
      "source-layer": "postal_sector_test",
      minzoom: 9,
      paint: {
        // "fill-outline-color": "#F5B49F",
        "fill-color": "#F5B49F",
        "fill-opacity": 1.0,
      },
    }
  }
}

export const pointStyle: CircleLayer = {
  id: "point",
  type: "circle",
  source: "point-source",
  // filter: ['!', ['has', 'point_count']], // Only show if it's NOT a cluster
  paint: {
    'circle-radius': [
      'interpolate',
      ['exponential', 1.5], // Smooth exponential scaling across zoom levels
      ['zoom'],
      5, 5,    // At zoom level 5 (or below), radius is 3px
      7, 8,   // At zoom level 7, radius is 6px
      10, 12,   // At zoom level 10, radius is 12px
      15, 24,  // At zoom level 15, radius is 24px
      20, 36   // At zoom level 20, radius is 36px
    ],
    "circle-color": ["coalesce", ["get", "color"], "#ececee"],
    'circle-stroke-width': 1,
    'circle-stroke-color': '#b4b4ba',
  },
};

export const clusterLayer: CircleLayer = {
  id: 'clusters',
  type: 'circle',
  source: 'point-source',
  filter: ['has', 'point_count'], // Only show if it's a cluster
  paint: {
    'circle-color': ['step', ['get', 'point_count'], '#b4b4ba', 50, '#d9d9dc', 250, '#ececee'],
    'circle-radius': ['step', ['get', 'point_count'], 20, 50, 30, 250, 40],
  },
};

export const clusterCountLayer: SymbolLayer = {
  id: 'cluster-count',
  type: 'symbol',
  source: 'point-source',
  filter: ['has', 'point_count'],
  layout: {
    'text-field': '{point_count_abbreviated}',
    'text-font': ['DIN Offc Pro Medium', 'Arial Unicode MS Bold'],
    'text-size': 12,
  },
  paint: {
    'text-color': '#000'
  }
}
